import React from 'react';
import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Variant selector. Reads `product.variants`:
 *   [{ type: 'size', value: 'M', label?: 'M', stock?: 5 },
 *    { type: 'color', value: '#000', label?: 'Noir', hex?: '#000', stock?: 8 }, ...]
 *
 * Groups variants by `type` and renders one selector group per type.
 *
 * Backwards-compatible: if `product.variants` is empty, returns null
 * (current sample products have no variants — the page just hides the section).
 */
export const VariantSelector = ({ variants = [], selected = {}, onChange }) => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  if (!variants || variants.length === 0) return null;

  // Group by type
  const groups = variants.reduce((acc, v) => {
    const type = v.type || 'option';
    if (!acc[type]) acc[type] = [];
    acc[type].push(v);
    return acc;
  }, {});

  const labelForType = (type) => {
    const dict = {
      size: T('Taille', 'Size'),
      color: T('Couleur', 'Colour'),
      material: T('Matière', 'Material'),
      style: T('Style', 'Style'),
      capacity: T('Capacité', 'Capacity'),
      option: T('Option', 'Option'),
    };
    return dict[type] || type;
  };

  return (
    <div className="space-y-5" data-testid="variant-selector">
      {Object.entries(groups).map(([type, opts]) => (
        <div key={type}>
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-sm font-semibold text-[#0F4C5C] uppercase tracking-wider">{labelForType(type)}</p>
            {selected[type] && (
              <p className="text-sm text-[#0F4C5C]/70">
                {opts.find((o) => o.value === selected[type])?.label || selected[type]}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2.5">
            {opts.map((opt) => {
              const active = selected[type] === opt.value;
              const oos = (opt.stock !== undefined && opt.stock <= 0);
              if (type === 'color' && (opt.hex || opt.value?.startsWith('#'))) {
                return (
                  <button
                    key={opt.value}
                    onClick={() => !oos && onChange(type, opt.value)}
                    disabled={oos}
                    className={`relative w-11 h-11 rounded-full border-2 transition-all ${
                      active ? 'border-[#0F4C5C] ring-2 ring-offset-2 ring-[#0F4C5C]' : 'border-[#0F4C5C]/15 hover:border-[#0F4C5C]/40'
                    } ${oos ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{ backgroundColor: opt.hex || opt.value }}
                    title={opt.label || opt.value}
                    data-testid={`variant-color-${opt.value.replace(/[^a-z0-9]/gi, '')}`}
                    aria-label={opt.label || opt.value}
                  >
                    {active && <Check className="w-4 h-4 text-white drop-shadow-md mx-auto" />}
                  </button>
                );
              }
              return (
                <button
                  key={opt.value}
                  onClick={() => !oos && onChange(type, opt.value)}
                  disabled={oos}
                  className={`min-w-[3rem] h-11 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                    active
                      ? 'border-[#0F4C5C] bg-[#0F4C5C] text-white'
                      : 'border-[#0F4C5C]/15 bg-white text-[#0F4C5C] hover:border-[#0F4C5C]/40'
                  } ${oos ? 'opacity-40 line-through cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`variant-option-${type}-${opt.value}`}
                >
                  {opt.label || opt.value}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
